/**
 * @file: useConstructionWorkItems.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  kPredefinedWorkItemsVersion,
  usePredefinedConstructionItems,
} from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/usePredefinedConstructionItems';
import { useConstructionWorkItemsFromInspectionResults } from '@euler/app/flows/task/pages/construction/functions/useConstructionWorkItemsFromInspectionResults';
import {
  ConstructionProcedure,
  ConstructionWorkItemDetail,
  PredefinedConstructionJobTemplate,
} from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { getPyInitials } from '@euler/utils/pinyin/getPyInitials';
import { nanoid } from 'nanoid/non-secure';
import { useEffect, useMemo } from 'react';

export type ConstructionWorkItemInfo = {
  name: string;
  categoryId: string;
  pyInitials: string;
  details: ConstructionWorkItemDetail[];
  procedures: ConstructionProcedure[];
  relatedFaultLikeSubjects?: string[];
};

export type ConstructionWorkItemGroup = {
  id: string;
  name: string;
  items: ConstructionWorkItemInfo[];
  template: PredefinedConstructionJobTemplate;
};

export type ConstructionWorkItemCategory = {
  id: string;
  name: string;
  groups: ConstructionWorkItemGroup[];
  template: PredefinedConstructionJobTemplate;
};

export const kDefaultConstructionWorkItemCategoryId = '__DEFAULT__';
export const kDefaultInspectionWorkItemsGroupId = '__INSPECTION_ITEMS__';
export const kDefaultPendingIssueWorkItemsGroupId = '__PENDING_ISSUE_ITEMS__';

export const kDefaultConstructionJobTemplate: PredefinedConstructionJobTemplate =
  {
    id: nanoid(),
    procedures: [
      {
        type: 'before',
        name: '检查情况',
        removable: undefined,
        allowChooseMediaFromGallery: undefined,
      },
      {
        type: 'new-prod',
        name: '新件确认',
        removable: undefined,
        allowChooseMediaFromGallery: undefined,
      },
      {
        type: 'custom',
        name: '施工过程',
        removable: undefined,
        allowChooseMediaFromGallery: undefined,
      },
      {
        type: 'old-prod',
        name: '旧件确认',
        removable: undefined,
        allowChooseMediaFromGallery: undefined,
      },
    ],
  };

type SynthesizedConstructionWorkItemInventory = {
  version: string;
  categories: ConstructionWorkItemCategory[];
};

export const useConstructionWorkItems = () => {
  const { cacheService } = useServiceFactory();

  const {
    state: predefinedConstructionItemsInventory,
    loading: isLoadingPredefinedConstructionItemsInventory,
    error: predefinedConstructionItemsInventoryError,
    invalidate: fetchPredefinedWorkItemsInventory,
    addCustomWorkItem,
    removeCustomWorkItem,
    clearCustomWorkItems,
  } = usePredefinedConstructionItems();

  const {
    state: workItemsFromInspectionResults,
    loading: isLoadingWorkItemsFromInspectionResults,
    error: workItemsFromInspectionResultsError,
    invalidate: fetchWorkItemsFromInspectionResults,
  } = useConstructionWorkItemsFromInspectionResults();

  useEffect(() => {
    // delete legacy cached data that is never used any more.
    void cacheService.remove('predefined-construction-workitems');
  }, [cacheService]);

  const workItemInventory = useMemo<
    SynthesizedConstructionWorkItemInventory | undefined
  >(() => {
    if (
      predefinedConstructionItemsInventory == null &&
      workItemsFromInspectionResults == null
    ) {
      return undefined;
    }

    const result: SynthesizedConstructionWorkItemInventory = {
      version: kPredefinedWorkItemsVersion,
      categories: [],
    };

    const defaultCategory: ConstructionWorkItemCategory = {
      name: '服务项目',
      id: kDefaultConstructionWorkItemCategoryId,
      groups: [],
      template: kDefaultConstructionJobTemplate,
    };
    result.categories.push(defaultCategory);

    //#region default & legacy issue groups

    // helper function that enumerates the work item details from inspection
    // results, and populate a work item group with item map information.
    const populateWorkItemGroupFromInspectionResults = (
      name: string,
      predict: (item: ConstructionWorkItemDetail) => boolean,
    ) => {
      const group: ConstructionWorkItemGroup = {
        id: kDefaultPendingIssueWorkItemsGroupId,
        name,
        items: [],
        template: defaultCategory.template,
      };
      const map = new Map<string, ConstructionWorkItemInfo>();
      for (const workItem of workItemsFromInspectionResults ?? []) {
        if (!workItem.maintenanceAdvice || !predict(workItem)) continue;
        if (!map.has(workItem.maintenanceAdvice)) {
          const item: ConstructionWorkItemInfo = {
            categoryId: defaultCategory.id,
            name: workItem.maintenanceAdvice,
            pyInitials: getPyInitials(workItem.maintenanceAdvice),
            details: [],
            procedures: group.template.procedures,
            relatedFaultLikeSubjects: workItem.relatedFaultLikeSubjects,
          };
          map.set(workItem.maintenanceAdvice, item);
          group.items.push(item);
        }
        map.get(workItem.maintenanceAdvice)!.details.push(workItem);
      }
      return [group, map] as const;
    };

    const [inspectionWorkItemGroup, workItemFromInspectionsMap] =
      populateWorkItemGroupFromInspectionResults('报修', x => !x.isLegacyIssue);

    const [pendingIssuesGroup, pendingIssueWorkItemMap] =
      populateWorkItemGroupFromInspectionResults(
        '遗留',
        x =>
          x.isLegacyIssue &&
          !workItemFromInspectionsMap.has(x.maintenanceAdvice),
      );

    defaultCategory.groups.push(inspectionWorkItemGroup, pendingIssuesGroup);

    //#endregion

    // groups from predefined work items.
    const added = new Set<string>();
    const predefinedCategories =
      predefinedConstructionItemsInventory?.categories ?? [];
    for (const predefinedCategory of predefinedCategories) {
      const category: ConstructionWorkItemCategory = {
        name: predefinedCategory.name,
        id: predefinedCategory.id,
        groups: [],
        template: predefinedCategory.template ?? {
          id: nanoid(),
          procedures: [],
        },
      };
      result.categories.push(category);

      for (const predefinedGroup of predefinedCategory.groups) {
        const group: ConstructionWorkItemGroup = {
          id: predefinedGroup.id,
          name: predefinedGroup.name,
          items: [],
          template:
            predefinedGroup.template ??
            predefinedCategory.template ??
            defaultCategory.template,
        };
        category.groups.push(group);
        for (const item of predefinedGroup.items) {
          // exclude this item if already presented in either
          // default or legacy groups for inspection results.
          const targetItem =
            workItemFromInspectionsMap.get(item.name) ??
            pendingIssueWorkItemMap.get(item.name);
          if (targetItem) {
            targetItem.procedures =
              item.template?.procedures ??
              predefinedGroup.template?.procedures ??
              defaultCategory.template.procedures;
            if (
              workItemFromInspectionsMap.has(item.name) &&
              !targetItem.procedures.some(x => x.type === 'before')
            ) {
              targetItem.procedures = [
                {
                  type: 'before',
                  name: '检查情况',
                  removable: undefined,
                  allowChooseMediaFromGallery: undefined,
                },
                ...targetItem.procedures,
              ];
            }
          } else if (!added.has(item.name)) {
            group.items.push({
              categoryId: category.id,
              name: item.name,
              pyInitials: getPyInitials(item.name),
              details: [],
              procedures: (
                item.template ??
                predefinedGroup.template ??
                predefinedCategory.template ??
                defaultCategory.template
              )?.procedures,
            });
            added.add(item.name);
          }
        }
      }
    }

    return result;
  }, [predefinedConstructionItemsInventory, workItemsFromInspectionResults]);

  return {
    isLoading:
      isLoadingPredefinedConstructionItemsInventory == true ||
      isLoadingWorkItemsFromInspectionResults,
    predefinedConstructionItemsInventoryError,
    workItemsFromInspectionResultsError,
    fetchPredefinedWorkItemsInventory,
    fetchWorkItemsFromInspectionResults,
    workItemInventory,
    addCustomWorkItem,
    removeCustomWorkItem,
    clearCustomWorkItems,
  };
};
